<template>
    <v-container>
        <form-toolbar :userRoles="userRoles" :canEdit="canEdit" :editMode="editMode" :assignment="assignment" @cancel-button-clicked="switchEditMode(false)" @edit-button-clicked="switchEditMode(true)"/>
        <v-form :readonly="readOnly" class="assignment-form">
            <v-card elevation="6">
                <div class="first-row">
                    <div v-if="this.assignmentId" class = "cancelled-wrapper">
                        <v-menu
                            v-model="menu2"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            left
                            offset-y
                            min-width="300"
                            class="filter-field"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="cancelledAt"
                                    class="as-built-date"
                                    label="Ακυρώθηκε στις"
                                    :disabled="malfunctionCompleted"
                                    :readonly="readOnly || (!!localAssignment.cancelledAt && isAdmin)"
                                    :clearable="!readOnly && (!localAssignment.cancelledAt || isAdmin)"
                                    @click:clear="cancelledAt = null"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <template v-slot:prepend-inner>
                                        <v-icon :color="theming.cardsAndTablesText || 'black'">mdi-calendar</v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker
                                v-model="cancelledAt"
                                color="#0047AB"
                                locale="el"
                                :disabled="readOnly || (!!localAssignment.cancelledAt && !isAdmin)"
                                @input="() => { menu2 = false }"
                            ></v-date-picker>
                        </v-menu>
                    </div>
                    <custom-checkbox
                        :readOnly="readOnly"
                        :label="'Προς Ακύρωση'"
                        :color="'red'"
                        :disabled="malfunctionCompleted"
                        change-back
                        :value="!!localAssignment.forCancelling"
                        @check-value-change="onForCancellingChange"
                    />
                    <custom-checkbox
                        v-if="localAssignment.type === 'PILOT'"
                        :readOnly="readOnly"
                        :label="'Smart Readiness'"
                        :color="'blue'"
                        change-back
                        :value="!!this.smartReadiness"
                        @check-value-change="onSmartReadinessChange"
                    />
                    <appointment-button class="appointment-btn" :currentUser="currentUser" :readOnly="false" :assignment="assignment" @appointment-refresh="() => this.$emit('assignment-refresh')"/>
                </div>
                <div class="first-row">
                    <v-checkbox
                        v-model="localAssignment.notFromImport"
                        label="Εξωσυστημική"
                        color="blue"
                    ></v-checkbox>
                    <v-checkbox
                        v-model="localAssignment.excavationDemand"
                        label="Αίτηση Εκσκαφής"
                        color="blue"
                    ></v-checkbox>
                    <v-checkbox
                        v-model="localAssignment.excavationPermitted"
                        label="Άδεια Εκσκαφής"
                        color="blue"
                    ></v-checkbox>
                </div>
                <v-textarea
                    v-if="cancelledAt || localAssignment.cancelledDescription"
                    v-model="localAssignment.cancelledDescription"
                    filled
                    label="Λόγος Ακύρωσης"
                    rows="10"
                    row-height="30"
                    :readonly="readOnly || (!!localAssignment.cancelledAt && !isAdmin)"
                    :clearable="!readOnly && (!localAssignment.cancelledAt)"
                    :rules="editMode ? rules: []"
                    class="field"
                ></v-textarea>
                <div class = "form-group-fields">
                    <v-select
                        v-model="localAssignment.client"
                        :items="clients"
                        return-object
                        item-text="name"
                        label="Πάροχος"
                        outlined
                        @change="onClientSelect"
                        class="field"
                        :disabled="!this.isAdminOrCompanyCoordinator"
                    ></v-select>
                    <v-select
                        v-model="localAssignment.type"
                        @change="onTypeSelect"
                        :items="types"
                        label="Τύπος"
                        outlined
                        :disabled="!this.localAssignment.client || !this.isAdminOrCompanyCoordinator"
                        class="field"
                    >
                        <template v-slot:selection="data" >
                            {{ typeNames[data.item] }}
                        </template>
                        <template v-slot:item="data" >
                            {{ typeNames[data.item] }}
                        </template>
                    </v-select>
                    <v-select
                        v-model="localAssignment.contractor"
                        :items="contractors"
                        return-object
                        item-text="name"
                        label="Εργολάβος"
                        outlined
                        :clearable="clearable"
                        :disabled="!localAssignment.client || !this.isAdminOrCompanyCoordinator"
                        class="field"
                    ></v-select>
                </div>
                <div class = "form-group-fields">
                    <v-text-field
                        v-model="localAssignment.sr"
                        label="SR"
                        type="text"
                        :clearable="clearable"
                        :error-messages="this.srErrorMessages"
                        :disabled="!this.localAssignment.client || !this.isAdminOrCompanyCoordinator"
                        @input="onSRInput"
                        class="field"
                    />
                    <v-text-field
                        v-model="localAssignment.clientName"
                        label="Ονοματεπώνυμο Πελάτη"
                        type="text"
                        :clearable="clearable"
                        :disabled="!this.localAssignment.client"
                        class="field"
                    />
                    <v-text-field
                        v-model="localAssignment.study"
                        label="Μελέτη"
                        type="text"
                        :clearable="clearable"
                        :disabled="!this.localAssignment.client"
                        class="field"
                    />
                    <v-select
                        v-model="localAssignment.clientFloor"
                        :items="availableFloors"
                        label="Όροφος Πελάτη"
                        outlined
                        :clearable="clearable"
                        class="field client-floor"
                    ></v-select>
                </div>
                <div class="form-group-fields">
                    <div class="building-id-wrapper">
                        <v-text-field
                            v-model="localAssignment.buildingId"
                            label="ID Κτιρίου"
                            type="text"
                            :clearable="clearable"
                            :disabled="!this.localAssignment.client"
                            class="field pr-0"
                        />
                        <v-icon v-if="localAssignment.sameBuildingId?.length" x-large class="realted-srs" @click="dialog2 = true">mdi-list-box</v-icon>
                        <v-dialog
                            v-model="dialog2"
                            width="700"
                        >
                            <v-card>
                                <v-card-title>Αναθέσεις Building ID</v-card-title>
                                <v-card v-for="(sameAssignment, index) of localAssignment.sameBuildingId" :key="index" elevation="3" class="same-building-assignment" @click="goToAssignment(sameAssignment.id)">
                                    <span class="bid-sr">{{ sameAssignment.sr }}</span>
                                    <assignment-status-label :status="sameAssignment.status" />
                                    <span>{{ typeNames[sameAssignment.type] }}</span>
                                </v-card>
                            </v-card>
                        </v-dialog>
                    </div>
                    <div class="location-wrapper">
                        <v-icon v-if="localAssignment.extraInfo?.coordinates" class="google-maps-icon mb-5" large @click.stop="openMapOnCoordinates(localAssignment.extraInfo.coordinates)">mdi-google-maps</v-icon>
                        <v-autocomplete
                            v-model="localAssignment.location"
                            :items="locations"
                            item-text="name"
                            return-object
                            :rules="editMode ? rules: []"
                            class="field"
                            label="Περιοχή"
                        ></v-autocomplete>
                    </div>
                    <v-text-field
                        v-model="localAssignment.address"
                        label="Οδός"
                        :rules="editMode ? rules: []"
                        type="text"
                        :disabled="!this.localAssignment.client"
                        :clearable="clearable"
                        class="field"
                        @click:append="onSameAddressAssignmentsClick"
                    >
                        <!-- @blur="onAddressBlur" -->
                        <template v-if="this.assignmentsWithSameAddress.length > 0" v-slot:append>
                            <v-icon color="#0047AB" @click="onSameAddressAssignmentsClick">
                                mdi-clipboard-arrow-down-outline
                            </v-icon>
                        </template>
                    </v-text-field>
                    <!-- <v-text-field
                        v-model="localAssignment.postCode"
                        label="TK"
                        type="text"
                        :disabled="!this.localAssignment.client"
                        :clearable="clearable"
                        :error-messages="!validatePostCode() ? 'Λάθος μορφή ΤΚ' : []"
                        class="field"
                    /> -->
                </div>
                <div class="form-group-fields">
                    <v-text-field
                        v-model="localAssignment.phoneNumber"
                        label="Τηλέφωνο"
                        type="phone"
                        :clearable="clearable"
                        :error-messages="!validatePhone() ? 'Λάθος μορφή τηλεφώνου' : []"
                        :disabled="!this.localAssignment.client"
                        class="field"
                    />
                    <v-text-field
                        v-model="localAssignment.manager"
                        label="Διαχειριστής"
                        type="text"
                        :clearable="clearable"
                        :disabled="!this.localAssignment.client"
                        class="field"
                    />
                </div>
                <div class="cabin-fields">
                    <v-text-field
                        v-model="localAssignment.cabin"
                        label="Καμπίνα"
                        type="text"
                        :clearable="clearable"
                        class="field cabin"
                        :disabled="!this.localAssignment.client"
                    />
                    <v-text-field
                        v-model="localAssignment.cabinAddress"
                        label="Διεύθυνση Καμπίνας"
                        type="text"
                        :clearable="clearable"
                        class="field cabin"
                        :disabled="!this.localAssignment.client"
                    />
                    <v-text-field
                        v-model="localAssignment.pipe"
                        label="Σωληνίσκος"
                        type="text"
                        :clearable="clearable"
                        class="field cabin"
                        :disabled="!this.localAssignment.client"
                    />
                </div>
                <div class = "form-group-fields">
                    <task-assignment
                        ref="task-assignment"
                        :editMode="editMode"
                        :contractorId="localAssignment.contractor?.id"
                        :alreadyAssignedTasks="alreadyAssignmentTasksMap"
                        :alreadyAssignedTasksLength="this.alreadyAssignmentTasksMap.size"
                        :assignmentTabs="getAvailableTabsBasedOnType"
                        @task-assignment-submited="onTaskAssignmentSubmit"/>
                </div>
                <v-textarea
                    v-model="localAssignment.description"
                    filled
                    label="Περιγραφή"
                    rows="3"
                    row-height="30"
                    :clearable="clearable"
                    class="field"
                ></v-textarea>
                <div class = "as-built-wrapper">
                    <div class="as-built-upload-wrapper">
                        <v-file-input
                            v-model="asBuiltFile"
                            accept=".zip,application/zip"
                            placeholder="Ανέβασμα PDF Αυτοψίας"
                            label="Ανέβασμα ZIP As Built"
                            prepend-icon=""
                            class="as-built-input"
                            :disabled="!editMode || localAssignment.forCancelling || cancelledAt"
                        >
                            <template v-slot:selection="{ text }">
                                <v-chip
                                    small
                                    label
                                    color="primary"
                                >
                                    {{ text }}
                                </v-chip>
                            </template>
                            <template v-slot:prepend-inner>
                                <v-icon :color="theming.cardsAndTablesText || 'black'">mdi-zip-box</v-icon>
                            </template>
                        </v-file-input>
                        <v-btn v-if="this.localAssignment.asBuiltSentAt" class="download-icon" @click="downloadAsBuiltFile()" fab small>
                            <v-icon color="blue" large >mdi-download</v-icon>
                        </v-btn>
                    </div>
                    <div v-if="this.localAssignment.asBuiltSentAt">Υποβλήθηκε το As Built στις:<b class="ml-2">{{ getDateTimeFormated(this.localAssignment.asBuiltSentAt) }}</b></div>
                    <custom-checkbox
                        v-if="this.localAssignment.type === 'MALFUNCTION'"
                        :readOnly="readOnly"
                        label="Ολοκληρωμένη"
                        :color="'green'"
                        :disabled="localAssignment.forCancelling || cancelledAt"
                        change-back
                        :value="!!malfunctionCompleted"
                        @check-value-change="onMalfunctionCompleted"
                    />
                </div>
            </v-card>
            <div v-if="canEdit || containsAny(['ROLE_ADMIN', 'ROLE_COORDINATOR'], userRoles)" class="submit-button-wrapper">
                <v-btn class="mx-2 white--text" :disabled="shouldDisableSubmit() " :loading="loading" large color="black" @click="onSubmit">
                    ΥΠΟΒΟΛΗ
                </v-btn>
            </div>
        </v-form>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import AssignmentStatusLabel from '@/components/AssignmentStatusLabel.vue'
    import assignmentService from '@/services/assignment'
    import client from '@/services/client'
    import location from '@/services/location'
    import Alert from '@/components/Alert'
    import momentTimezone from 'moment-timezone'
    import FormToolbar from '@/components/FormToolbar'
    import TaskAssignment from '@/components/TaskAssignment'
    import CustomCheckbox from '@/components/CustomCheckBox'
    import AppointmentButton from '@/components/AppointmentButton'
    import uploadService from '@/services/upload'
    import file from '@/utils/file'
    const HETZNER_API_STORAGE_URL = process.env.VUE_APP_HETZNER_API_STORAGE_URL
    const COMPANY_NAME = process.env.VUE_APP_ENV_COMPANY_NAME

    export default {
        name: 'AssignmentForm',
        components: { Alert, FormToolbar, TaskAssignment, CustomCheckbox, AppointmentButton, AssignmentStatusLabel },
        props: {
            assignment: {
                type: Object,
                default: () => { return {} },
                required: true
            },
            currentUser: {
                type: Object,
                default: () => { return {} },
                required: true
            },
            canEdit: {
                type: Boolean,
                required: true
            }
        },
        data: () => {
            return {
                dialog2: false,
                tempCancelled: false,
                localAssignment: {},
                showAlert: false,
                malfunctionCompleted: false,
                message: null,
                type: null,
                loading: false,
                menu: false,
                mode: null,
                asBuiltFile: null,
                menu2: false,
                readOnly: false,
                clearable: true,
                clients: [],
                contractors: [],
                smartReadiness: false,
                hetznerApiHost: HETZNER_API_STORAGE_URL,
                locations: [],
                isCancelled: false,
                assigneeWorkAssignmentMap: new Map(),
                alreadyAssignmentTasksMap: new Map(),
                cancelledAt: null,
                rules: [
                    v => !!v || 'Το πεδίο είναι υποχρεωτικό'
                ],
                srExists: false,
                editMode: false,
                clientTabs: [],
                appointmentDialog: false,
                assignmentsWithSameAddress: [],
                types: [],
                srErrorMessages: []
            }
        },
        watch: {
            assignment: {
                handler() {
                    this.resetForm()
                },
                immediate: true
            }
        },
        computed: {
            userRoles() {
                return this.currentUser?.roles?.map(role => role.role) || []
            },
            availableFloors() {
                return this.config?.buildingFloors || []
            },
            pilotTabs() {
                return [{ value: 'assignment', name: 'ΑΝΑΘΕΣΗ' }, ...this.config?.stagesPerAssignmentType['PILOT'] || []]
            },
            pilotUfbbTabs() {
                return [{ value: 'assignment', name: 'ΑΝΑΘΕΣΗ' }, ...this.config?.stagesPerAssignmentType['PILOT_UFBB'] || []]
            },
            lastDropTabs() {
                return [{ value: 'assignment', name: 'ΑΝΑΘΕΣΗ' }, ...this.config?.stagesPerAssignmentType['LAST_DROP'] || []]
            },
            malfunctionTabs() {
                return [{ value: 'assignment', name: 'ΑΝΑΘΕΣΗ' }, ...this.config?.stagesPerAssignmentType['MALFUNCTION'] || []]
            },
            getAvailableTabsBasedOnType() {
                switch (this.localAssignment.type) {
                case 'PILOT': return this.pilotTabs.map(tab => tab.value).filter(tab => this.clientTabs.includes(tab) && tab !== 'assignment')
                case 'PILOT_UFBB': return this.pilotUfbbTabs.map(tab => tab.value).filter(tab => this.clientTabs.includes(tab) && tab !== 'assignment')
                case 'LAST_DROP': return this.lastDropTabs.map(tab => tab.value).filter(tab => this.clientTabs.includes(tab) && tab !== 'assignment')
                case 'MALFUNCTION': return this.malfunctionTabs.map(tab => tab.value).filter(tab => this.clientTabs.includes(tab) && tab !== 'assignment')
                default: return ['assignment']
                }
            },
            shouldEditTaskAssignment() {
                return this.localAssignment.contractor
            },
            isAdminOrCompanyCoordinator() {
                return this.isAdmin || (this.isCoordinator && this.userContractor.toLowerCase() === COMPANY_NAME.toLowerCase())
            },
            theming() {
                return this.$store.getters.getTheming || {}
            },
            isMalfunction() {
                return this.localAssignment.type === 'MALFUNCTION'
            },
            userContractor() {
                return this.currentUser?.contractor.name || ''
            },
            isAdmin() {
                return this.containsAny(['ROLE_ADMIN'], this.userRoles)
            },
            isCoordinator() {
                return this.containsAny(['ROLE_COORDINATOR'], this.userRoles)
            },
            config() {
                return this.$store.getters.getConfig || {}
            },
            typeNames() {
                return Object.fromEntries(
                    this.config?.assignmentTypes?.map(item => [item.value, item.name])
                )
            },
            invalidPilotUfbbSR() {
                return this.localAssignment.type === 'PILOT_UFBB' && !this.localAssignment.sr?.startsWith('UH-')
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'edit') {
                this.readOnly = true
                this.clearable = false
            } else if (this.mode === 'create') {
                this.editMode = true
            }
            this.assignmentId = this.$route.params.assignmentId
            this.localAssignment = { ...this.assignment }
            this.getAllClients()
            this.getAllLocations()
        },
        mounted() {
            this.initAlreadyAssignmentTasksMap()
        },
        methods: {
            isUrl(str) {
                try {
                    const url = new URL(str)
                    return url.protocol === 'http:' || url.protocol === 'https:'
                } catch {
                    return false
                }
            },
            onMalfunctionCompleted(e) {
                this.malfunctionCompleted = e
            },
            downloadAsBuiltFile() {
                if (!this.localAssignment.asBuiltFileUrl) {
                    return
                }
                if (this.isUrl(this.localAssignment.asBuiltFileUrl)) {
                    file.downloadFile(this.localAssignment.asBuiltFileUrl)
                } else {
                    file.downloadFile(process.env.VUE_APP_HETZNER_API_STORAGE_URL + '/' + this.localAssignment.asBuiltFileUrl)
                }
            },
            resetForm() {
                this.localAssignment = { ...this.assignment } || {}
                if (this.localAssignment.client) {
                    this.contractors = this.localAssignment.client.contractors
                    this.types = this.localAssignment.client.assignmentTypes
                    this.clientTabs = this.localAssignment.client.assignmentTabs
                }
                // this.asBuiltSentAt = this.localAssignment.asBuiltSentAt ? this.getDateFormated(this.localAssignment.asBuiltSentAt) : null
                this.cancelledAt = this.localAssignment.cancelledAt ? this.getDateFormated(this.localAssignment.cancelledAt) : null
                this.initAlreadyAssignmentTasksMap()
                this.smartReadiness = this.localAssignment.smartReadiness
                if (this.localAssignment.status === 'COMPLETED' && this.isMalfunction) {
                    this.malfunctionCompleted = true
                }
            },
            onSmartReadinessChange(e) {
                this.localAssignment.smartReadiness = e
            },
            onForCancellingChange(e) {
                this.localAssignment.forCancelling = e
            },
            openMapOnCoordinates(coordinates) {
                window.open(`https://www.google.com/maps?q=${coordinates.x},${coordinates.y}`, '_blank')
            },
            onCancelledChange(e) {
                this.isCancelled = e
            },
            cancelAssignment() {
                this.dialog = false
            },
            cancelTheCancelling() {
                this.localAssignment.cancelled = !this.localAssignment.cancelled
                this.dialog = false
            },
            goToAssignment(id) {
                this.$router.push(`/assignments/${id}/edit?tab=assignment`)
            },
            onSameAddressAssignmentsClick() {
                if (this.assignmentsWithSameAddress.length === 0) {
                    return
                }
                window.open(encodeURI(window.location.origin + '/assignments?sr=' + this.assignmentsWithSameAddress.map(a => a.sr)), '_blank')
            },
            // onAddressBlur() {
            //     if (this.editMode && this.localAssignment.address.length > 0) {
            //         this.assignmentsWithSameAddress = []
            //         this.findAssignmentSameAddress()
            //     }
            // },
            onTypeSelect(type) {
                this.localAssignment.type = type
                this.localAssignment = { ...this.localAssignment }
            },
            initAlreadyAssignmentTasksMap() {
                this.alreadyAssignmentTasksMap = new Map()
                this.localAssignment?.taskAssignments?.forEach(taskAssignment => {
                    const assigneeId = taskAssignment.assignee.id
                    const assigneeTaskAssignments = this.alreadyAssignmentTasksMap.get(assigneeId) || []
                    assigneeTaskAssignments.push(taskAssignment)
                    this.alreadyAssignmentTasksMap.set(assigneeId, assigneeTaskAssignments)
                    this.assigneeWorkAssignmentMap.set(assigneeId, assigneeTaskAssignments)
                })
            },
            onTaskAssignmentSubmit(assigneeId, taskAssignments) {
                this.assigneeWorkAssignmentMap.set(assigneeId, taskAssignments)
            },
            getDateFormated(date) {
                return momentTimezone(date).tz('Europe/Athens').format('YYYY-MM-DD').toString()
            },
            getDateTimeFormated(date) {
                return momentTimezone(date).tz('Europe/Athens').format('YYYY-MM-DD HH:mm:ss').toString()
            },
            onClientSelect(cl) {
                const selectedClient = this.clients.find(client => client.id === cl.id)
                this.localAssignment.contractor = null
                if (selectedClient) {
                    this.contractors = selectedClient.contractors
                    this.types = selectedClient.assignmentTypes
                    this.clientTabs = selectedClient.assignmentTabs
                    this.localAssignment.type = this.types[0] || null
                }
            },
            tabSelect(tab) {
                this.selectedTab = tab
            },
            switchEditMode(editMode) {
                if (!this.canEdit) {
                    return
                }
                if (editMode) {
                    this.readOnly = false
                    this.clearable = true
                    this.editMode = true
                } else {
                    this.readOnly = true
                    this.clearable = false
                    this.editMode = false
                }
            },
            getAllLocations() {
                location.getAll().then(resp => {
                    this.locations = resp.data
                })
            },
            getAllClients() {
                client.getAllClients().then(resp => {
                    this.clients = resp.data
                    if (this.clients.length === 1) {
                        this.localAssignment.client = this.clients[0]
                    }
                    if (this.localAssignment.client) {
                        this.contractors = this.localAssignment.client.contractors
                        this.types = this.localAssignment.client.assignmentTypes
                    }
                })
            },
            validatePhone() {
                if (!this.localAssignment.phoneNumber) {
                    return true
                }
                const telephones = this.localAssignment.phoneNumber.split(',')
                let patt = new RegExp(/^\d+$/)
                for (let tel of telephones) {
                    if (!patt.test(tel) || tel?.length !== 10) {
                        return false
                    }
                }
                return true
            },
            async onSRInput() {
                this.srErrorMessages = []
                if (!this.localAssignment.sr) {
                    this.srErrorMessages.push('Το SR δεν γίνεται να είναι κενό')
                } else if (this.localAssignment.type === 'PILOT_UFBB' && !this.localAssignment.sr.startsWith('UH-')) {
                    this.srErrorMessages.push('Λάθος format SR για Pilot UFBB')
                } else {
                    let srExists
                    try {
                        await assignmentService.getAssignmentBySr(this.localAssignment.sr)
                        srExists = true
                    } catch (e) {
                        srExists = false
                    }
                    if (srExists) {
                        this.srErrorMessages.push('Ο συγκεκριμένος SR χρησιμοποιείται ήδη')
                    }
                }
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                if (this.srErrorMessages.length > 0) {
                    return true
                }
                if (this.cancelledAt && !this.localAssignment.cancelledDescription) {
                    return true
                }
                return !(this.localAssignment.client && this.localAssignment.clientName && this.localAssignment.address && this.localAssignment.location && this.validatePhone() && (this.editMode || this.mode === 'create') && (!this.localAssignment.cancelledAt || this.isAdmin))
            },
            createAssignment() {
                this.loading = true
                let temp = []
                this.assigneeWorkAssignmentMap.values().forEach(value => { temp = temp.concat(value || []) })
                this.localAssignment.taskAssignments = temp
                assignmentService.createAssignment(this.localAssignment).then(resp => {
                    if (resp.id) {
                        this.assignmentId = resp.id
                    }
                    this.loading = false
                    this.$router.push('/assignments')
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error?.response?.data?.message || 'Προέκυψε κάποιο σφάλμα. Παρακαλώ επικοινωνήστε με τον Διαχειριστή', 'error')
                })
            },
            async editAssignment() {
                this.loading = true
                if (this.cancelledAt) {
                    this.localAssignment.cancelledAt = momentTimezone(this.cancelledAt).tz('Europe/Athens').startOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
                } else {
                    this.localAssignment.cancelledAt = null
                }
                if (this.asBuiltFile) {
                    this.localAssignment.asBuiltFileUrl = await this.uploadAsBuiltFile()
                }
                let temp = []
                if (this.malfunctionCompleted && this.localAssignment.type === 'MALFUNCTION') {
                    this.localAssignment.malfunctionCompleted = this.malfunctionCompleted
                }
                this.assigneeWorkAssignmentMap.values().forEach(value => { temp = temp.concat(value || []) })
                this.localAssignment.taskAssignments = temp
                this.localAssignment.cancelled = this.localAssignment.cancelled || this.isCancelled
                assignmentService.updateAssignmentTab('assignment', this.localAssignment.id, { assignment: this.localAssignment }).then((response) => {
                    this.loading = false
                    this.setAlert('Η ενημέρωση της ανάθεσης ήταν επιτυχημένη', 'success')
                    this.switchEditMode(false)
                    this.$emit('assignment-refresh')
                }).catch((error) => {
                    this.setAlert(error?.response?.data?.message || 'Προέκυψε κάποιο σφάλμα. Παρακαλώ επικοινωνήστε με τον Διαχειριστή', 'error')
                    this.loading = false
                })
            },
            async onSubmit() {
                if (this.mode === 'edit') {
                    this.editAssignment()
                } else if (this.mode === 'create') {
                    this.createAssignment()
                }
            },
            containsAny(arr1, arr2) {
                return arr1.some(item => arr2.includes(item))
            },
            async uploadAsBuiltFile() {
                let formData = new FormData()
                formData.append('file', this.asBuiltFile)
                const response = await uploadService.uploadFile(this.assignment.sr + '/as-built', formData)
                if (response.status === 200) {
                    this.asBuiltFile = null
                    return response.data.content
                }
                return null
            }
        }
    }
</script>
<style scoped>
.bid-sr {
    width: 140px;
}
.same-building-assignment {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    margin-bottom: 0.5rem;
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
}
.same-building-assignment:hover {
    transform: scale(1.01);
}
.building-id-wrapper {
    display: flex;
    margin-right: 1rem;
    margin-bottom: 0.5rem;
    max-width: 300px;
}
.realted-srs {
    color: #0000A3;
    cursor: pointer;
}
.realted-srs:hover {
    color: #0000a359;
}
.as-built-upload-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
}
.client-floor {
    max-width: 300px;
    margin: 0 auto;
}
.first-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
}
.v-list-item__title {
    color: black !important;
}
.field {
    padding: 0.5rem;
}
.appointment-btn {
    align-self: center;
}
.field.cancel-checkbox {
    font-weight: 600;
}

.form-group-fields {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
}

.field.type {
    max-width: 250px;
}

.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.location-wrapper {
    display: flex;
    align-content: flex-start;
}
.google-maps-icon {
    color: rgb(138, 0, 0);
}

.google-maps-icon:hover {
    color: red;
}
.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}

.edit-button-wrapper {
    display: flex;
    justify-content: flex-start;
}

.v-card {
    padding: 0.8rem;
    color: var(--cards-and-tables-text-color) !important;
    background: var(--cards-and-tables-color) !important;
}

.as-built-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.as-built-input {
    min-width: 250px;
    max-width: 350px;
}
.cabin-fields {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
}

.as-built-date {
    max-width: 300px;
}
@media only screen and (max-width: 436px) {
  .same-building-assignment {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
}
</style>
<style>
.v-date-picker-title {
    height: 70px;
}
</style>
